import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface ViewMenuAnchor {
  href: string;
  /** Viewport coordinates of the pointer, or of the element for a keyboard opening. */
  x: number;
  y: number;
  /** Where focus goes back to when the menu closes without navigating. */
  returnFocus: HTMLElement | null;
}

interface ViewContextMenuProps {
  anchor: ViewMenuAnchor;
  onAddView: () => void;
  onClose: () => void;
}

const MENU_WIDTH = 208;
const VIEWPORT_MARGIN = 8;

/**
 * The right-click menu on anything that opens a resource (project 13D).
 *
 * "Add view" leads because it is why the menu exists. The browser's own two entries a
 * link would have offered stay here too — this menu replaces the native one on these
 * elements, so it must not take away what the native one gave. "Add view" names the
 * dashboard's own concept on purpose: next to "Open in browser tab", an "Open in new
 * tab" would read as the same thing twice.
 *
 * Same surface and tokens as `GenericRowActionsMenu`; its own component because it
 * opens at a point, not under a trigger, and it drives focus itself (roving, Esc).
 */
export default function ViewContextMenu({ anchor, onAddView, onClose }: ViewContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const [position, setPosition] = useState({ top: anchor.y, left: anchor.x });
  const [copied, setCopied] = useState(false);

  const close = useCallback(
    (restoreFocus: boolean) => {
      onClose();
      if (restoreFocus) anchor.returnFocus?.focus?.();
    },
    [onClose, anchor.returnFocus]
  );

  // Keep the menu on screen: flip left / up when the pointer is near an edge.
  useLayoutEffect(() => {
    const height = menuRef.current?.offsetHeight ?? 0;
    const left = Math.min(anchor.x, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN);
    const top =
      anchor.y + height + VIEWPORT_MARGIN > window.innerHeight
        ? Math.max(VIEWPORT_MARGIN, anchor.y - height)
        : anchor.y;
    setPosition({ top, left: Math.max(VIEWPORT_MARGIN, left) });
  }, [anchor.x, anchor.y]);

  useEffect(() => {
    itemsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      close(false);
    };
    const onDismiss = () => close(false);
    document.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);
    window.addEventListener("blur", onDismiss);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
      window.removeEventListener("blur", onDismiss);
    };
  }, [close]);

  const absoluteHref = `${window.location.origin}${anchor.href}`;

  const actions = [
    {
      id: "add-view",
      label: "Add view",
      run: () => {
        onAddView();
        close(true);
      },
    },
    {
      id: "browser-tab",
      label: "Open in browser tab",
      run: () => {
        window.open(absoluteHref, "_blank", "noopener,noreferrer");
        close(false);
      },
    },
    {
      id: "copy-link",
      label: copied ? "Link copied" : "Copy link",
      run: async () => {
        try {
          await navigator.clipboard.writeText(absoluteHref);
          setCopied(true);
          window.setTimeout(() => close(true), 600);
        } catch {
          close(true);
        }
      },
    },
  ];

  const onKeyDown = (event: React.KeyboardEvent) => {
    const index = itemsRef.current.findIndex((item) => item === document.activeElement);
    const count = actions.length;
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      itemsRef.current[(index + 1) % count]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      itemsRef.current[(index - 1 + count) % count]?.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      itemsRef.current[0]?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      itemsRef.current[count - 1]?.focus();
    } else if (event.key === "Tab") {
      event.preventDefault();
      close(true);
    }
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="Resource actions"
      data-testid="view-context-menu"
      onKeyDown={onKeyDown}
      onContextMenu={(event) => event.preventDefault()}
      style={{ position: "fixed", top: position.top, left: position.left, width: MENU_WIDTH, zIndex: 9999 }}
      className="rounded-lg border border-semantic-border-primary bg-semantic-bg-primary p-1 shadow-lg"
    >
      {actions.map((action, index) => (
        <React.Fragment key={action.id}>
          {index === 1 && <div role="separator" className="my-1 h-px bg-semantic-border-secondary" />}
          <button
            ref={(element) => {
              itemsRef.current[index] = element;
            }}
            type="button"
            role="menuitem"
            onClick={action.run}
            className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-semantic-text-primary hover:bg-semantic-state-active focus:bg-semantic-state-active focus:outline-none ${
              index === 0 ? "font-semibold" : ""
            }`}
          >
            <span>{action.label}</span>
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body
  );
}
